import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import type {
  BespokeTemplateResponse,
  FormSchemaVersion,
} from '../../services/api/types.gen';
import type { RogerDocFormType } from '../../services/api/models/RogerDocFormType';
import { FormSchemaSectionNav } from './FormSchemaSectionNav';
import { SectionLayoutView } from './SectionLayoutView';
import type { PreviewViewport } from './previewViewport';
import { formatSectionDropdownLabel } from './formatSectionDropdownLabel';
import type { FormTree, FormTreeQuestion } from './formSchemaTypes';
import { getSectionLayoutState } from './formSchemaUtils';
import type { SectionConsistency } from './sectionConsistency';

export function FormSchemaStudio({
  selectedRogerDocFormType,
  selectedVersion,
  selectedSectionName,
  formTree,
  formTreeLabelByCategory,
  orderedFormTreeSections,
  treeLoading,
  templateBySection,
  getSectionConsistency,
  onSelectSection,
  onCopyLayout,
  onViewQuestions,
  previewViewport,
  onPreviewViewportChange,
}: {
  selectedRogerDocFormType: RogerDocFormType;
  selectedVersion: FormSchemaVersion;
  selectedSectionName: string | null;
  formTree: FormTree | null;
  formTreeLabelByCategory: Record<string, string>;
  orderedFormTreeSections: string[];
  treeLoading: boolean;
  templateBySection: Map<string, BespokeTemplateResponse>;
  getSectionConsistency: (section: string) => SectionConsistency;
  onSelectSection: (sectionName: string) => void;
  onCopyLayout: (template: BespokeTemplateResponse | null) => void;
  onViewQuestions: (section: string, questions: FormTreeQuestion[]) => void;
  // Owned by the workspace so form-type switches (which clear selectedVersion
  // and remount this studio) do not reset the user's phone/tablet preview.
  previewViewport: PreviewViewport;
  onPreviewViewportChange: (viewport: PreviewViewport) => void;
}) {
  const [layoutJsonOpen, setLayoutJsonOpen] = useState(false);

  const selectSection = (sectionName: string) => {
    setLayoutJsonOpen(false);
    onSelectSection(sectionName);
  };

  if (treeLoading) {
    return (
      <div className='pt-4'>
        <div className='flex items-center justify-center py-8'>
          <Loader2 className='h-5 w-5 animate-spin' />
        </div>
      </div>
    );
  }

  if (!formTree) {
    return (
      <div className='pt-4'>
        <p className='text-muted-foreground text-center py-8'>
          Failed to load form tree.
        </p>
      </div>
    );
  }

  if (orderedFormTreeSections.length === 0) {
    return (
      <div className='pt-4'>
        <p className='text-muted-foreground text-center py-8'>
          This schema version has no sections.
        </p>
      </div>
    );
  }

  const labelFor = (section: string) =>
    formatSectionDropdownLabel(section, formTreeLabelByCategory);
  const selectedQuestions =
    selectedSectionName != null ? formTree[selectedSectionName] : undefined;

  return (
    <div className='pt-4'>
      <div className='flex min-h-[32rem] flex-col gap-3 md:flex-row'>
        <FormSchemaSectionNav
          sections={orderedFormTreeSections}
          labelFor={labelFor}
          selectedSection={selectedSectionName}
          onSelect={selectSection}
          getLayoutState={section =>
            getSectionLayoutState(
              templateBySection.get(section),
              formTree[section] ?? []
            )
          }
          getConsistency={getSectionConsistency}
          onCopyLayout={section =>
            onCopyLayout(templateBySection.get(section) ?? null)
          }
          onViewQuestions={section =>
            onViewQuestions(section, formTree[section] ?? [])
          }
          onOpenLayoutJson={section => {
            selectSection(section);
            setLayoutJsonOpen(true);
          }}
        />
        <div className='min-w-0 flex-1 md:pl-2'>
          {selectedSectionName && selectedQuestions ? (
            <SectionLayoutView
              key={`${selectedVersion.name}::${selectedSectionName}`}
              template={templateBySection.get(selectedSectionName) ?? null}
              formType={selectedRogerDocFormType}
              schemaVersion={selectedVersion}
              sectionName={selectedSectionName}
              sectionLabel={labelFor(selectedSectionName)}
              questions={selectedQuestions}
              layoutJsonOpen={layoutJsonOpen}
              onLayoutJsonOpenChange={setLayoutJsonOpen}
              previewViewport={previewViewport}
              onPreviewViewportChange={onPreviewViewportChange}
              onCopyLayout={onCopyLayout}
            />
          ) : (
            <div className='flex min-h-[24rem] items-center justify-center text-sm text-muted-foreground'>
              Select a section to view its layout.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
